/**
 * Copyright © 2026 Adnuntius AS.
 */
import {ChangeDetectionStrategy, Component, DestroyRef, Inject, Input, OnChanges, OnDestroy, SimpleChanges, inject, signal} from '@angular/core';
import {TranslateService} from '@ngx-translate/core';
import * as _ from 'lodash';
import template from './advertiser-information-page.component.html';
import {BufferedSignalValue, bufferTemplateSignalValue, createBufferedSignalValue} from '../common/util/template-state';

import {LocalNetworkInfo} from '../../components/session/local-network-info';
import {advertiserNavigationParams} from '../../components/util/navigation-context';
import {OrderApiService, OrderModel} from '../order/order-api.service';

import {LocalNetworkProfile} from '../../components/session/local-network-profile';
import {LocalUserPermissions} from '../../components/session/local-user-permissions';
import {StateService} from '@uirouter/core';
import {SignalAdminResourceListState, SignalAdminResourceListStateFactory} from '../common/controller/signal-admin-resource-list-state';
import {FolioApiService} from '../folio/folio-api.service';
import {CreativeSetApiService} from '../creative-set/creative-set-api.service';
import {CreativeApiService, CreativeModel} from '../creative/creative-api.service';
import {LineItemApiService, LineItemModel} from '../line-item/line-item-api.service';
import {AdvertisingSharedModule} from '../advertising/advertising-shared.module';
import {CreativeModule} from '../creative/creative.module';
import {LineItemModule} from '../line-item/line-item.module';
import {OrderModule} from '../order/order.module';
import {FolioListPageComponent} from '../folio/folio-list-page.component';
import {CreativeSetListPageComponent} from '../creative-set/creative-set-list-page.component';


type AdvertiserInformationTab = 'orders' | 'lineItems' | 'folios' | 'creativeSets' | 'creatives';

function emptyCountList(): {totalCountText: string} {
  // Seed list count state before the list helper is attached.
  return {totalCountText: ''};
}

@Component({
  standalone: true,
  imports: [
    AdvertisingSharedModule,
    CreativeModule,
    LineItemModule,
    OrderModule,
    FolioListPageComponent,
    CreativeSetListPageComponent
  ],
  selector: 'adn-advertiser-information-page',
  template,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AdvertiserInformationPageComponent implements OnChanges, OnDestroy {
  @Input() model: any;
  private _isNewDirect: boolean | undefined;
  private _readOnlyDirect: boolean | undefined;

  @Input()
  set isNew(value: boolean) {
    this._isNewDirect = value;
  }

  get isNew(): boolean {
    return this._isNewDirect;
  }

  @Input()
  set readOnly(value: boolean) {
    this._readOnlyDirect = value;
  }

  get readOnly(): boolean {
    return this._readOnlyDirect;
  }

  effectiveIsNew = false;
  activeTab: AdvertiserInformationTab = 'orders';

  showOrders = false;
  showCreativeSets = false;
  showFolios = false;
  showCreatives = false;

  readonly orders = signal<SignalAdminResourceListState<OrderModel> | null>(null);
  readonly lineItems = signal<SignalAdminResourceListState<LineItemModel> | null>(null);
  readonly folios = signal<any>(null);
  readonly creativeSets = signal<any>(null);
  readonly creatives = signal<SignalAdminResourceListState<CreativeModel> | null>(null);

  private listInitTimer?: ReturnType<typeof setTimeout>;
  private destroyed = false;
  private readonly orderListState = createBufferedSignalValue<SignalAdminResourceListState<OrderModel> | null>(null);
  private readonly lineItemListState = createBufferedSignalValue<SignalAdminResourceListState<LineItemModel> | null>(null);
  private readonly folioListState = createBufferedSignalValue<any>(null);
  private readonly creativeSetListState = createBufferedSignalValue<any>(null);
  private readonly creativeListState = createBufferedSignalValue<SignalAdminResourceListState<CreativeModel> | null>(null);
  private readonly orderCountState = createBufferedSignalValue(0);
  private readonly lineItemCountState = createBufferedSignalValue(0);
  private readonly folioCountState = createBufferedSignalValue(0);
  private readonly creativeSetCountState = createBufferedSignalValue(0);
  private readonly creativeCountState = createBufferedSignalValue(0);
  private readonly signalListFactory = inject(SignalAdminResourceListStateFactory);
  private readonly folioApi = inject(FolioApiService);
  private readonly creativeSetApi = inject(CreativeSetApiService);
  private readonly creativeApi = inject(CreativeApiService);
  private readonly lineItemApi = inject(LineItemApiService);
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    @Inject(LocalNetworkProfile) private readonly LocalNetworkProfile: any,
    private readonly translate: TranslateService,
    private readonly $state: StateService,
    private readonly orderApi: OrderApiService,
    @Inject(LineItemApiService) private readonly LineItem: LineItemApiService,
    @Inject(LocalUserPermissions) private readonly LocalUserPermissions: any,
    private readonly localNetworkInfo: LocalNetworkInfo = new LocalNetworkInfo()
  ) {
    const sectionPerms = LocalUserPermissions.getSectionPerms();
    this.showOrders = !!sectionPerms.orders;
    this.showCreativeSets = !!sectionPerms.creativeSets && this.localNetworkInfo.getInfo('creativeSetCount') > 0;
    this.showFolios = !!sectionPerms.folios && this.localNetworkInfo.getInfo('folioCount') > 0;
    this.showCreatives = !!sectionPerms.creatives;
    if (this.showOrders) {
      this.activeTab = 'orders';
    } else if (this.showCreatives) {
      this.activeTab = 'lineItems';
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.isNew) {
      this.effectiveIsNew = !!this.isNew;
    }
    if (changes.model || changes.isNew) {
      this.scheduleListInit();
    }
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.listInitTimer) {
      clearTimeout(this.listInitTimer);
    }
  }

  selectTab(tab: AdvertiserInformationTab): void {
    this.activeTab = tab;
  }

  get displayOrders(): SignalAdminResourceListState<OrderModel> | null {
    return this.bufferList(this.orderListState, this.orders());
  }

  get displayLineItems(): SignalAdminResourceListState<LineItemModel> | null {
    return this.bufferList(this.lineItemListState, this.lineItems());
  }

  get displayFolios(): any {
    return this.bufferList(this.folioListState, this.folios());
  }

  get displayCreativeSets(): any {
    return this.bufferList(this.creativeSetListState, this.creativeSets());
  }

  get displayCreatives(): SignalAdminResourceListState<CreativeModel> | null {
    return this.bufferList(this.creativeListState, this.creatives());
  }

  get displayOrderCount(): number {
    return this.bufferListCount(this.orderCountState, this.orders());
  }

  get displayLineItemCount(): number {
    return this.bufferListCount(this.lineItemCountState, this.lineItems());
  }

  get displayFolioCount(): number {
    return this.bufferListCount(this.folioCountState, this.folios());
  }

  get displayCreativeSetCount(): number {
    return this.bufferListCount(this.creativeSetCountState, this.creativeSets());
  }

  get displayCreativeCount(): number {
    return this.bufferListCount(this.creativeCountState, this.creatives());
  }

  newOrder(): void {
    this.$state.go('app.adv.orders.new', advertiserNavigationParams(this.model));
  }

  newCreative(): void {
    this.$state.go('app.adv.creatives.new', advertiserNavigationParams(this.model));
  }

  newBulkCreative(): void {
    this.$state.go('app.adv.creatives.bulkupload', advertiserNavigationParams(this.model));
  }

  private initLists(): void {
    if (!this.model?.id || this.effectiveIsNew) {
      this.orders.set(null);
      this.lineItems.set(null);
      this.folios.set(null);
      this.creativeSets.set(null);
      this.creatives.set(null);
      return;
    }

    const orders = this.signalListFactory.create(this.orderApi, {
      listId: 'fullOrdersForAdvertiser', baseTable: 'order', syncPageToUrl: false,
      additionalParams: {advertiser: this.model.id}
    }, this.destroyRef);
    orders.readOnly.set(!!this.readOnly);
    (orders as any).limitedFilter = true;
    this.orders.set(orders);
    orders.load();

    const lineItems = this.signalListFactory.create(this.lineItemApi, {
      listId: 'fullLineItemsForAdvertiser', baseTable: 'lineItem', syncPageToUrl: false,
      additionalParams: {advertiser: this.model.id}
    }, this.destroyRef);
    lineItems.readOnly.set(!!this.readOnly);
    this.lineItems.set(lineItems);
    lineItems.load();

    this.folios.set(null);
    if (this.localNetworkInfo.getInfo('folioCount') > 0) {
      const folios = this.signalListFactory.create(this.folioApi, {
        listId: 'fullFoliosForAdvertiser', baseTable: 'folio', syncPageToUrl: false,
        additionalParams: {advertiser: this.model.id}
      }, this.destroyRef);
      folios.readOnly.set(!!this.readOnly);
      this.folios.set(folios);
      folios.load();
    }

    this.creativeSets.set(null);
    if (this.localNetworkInfo.getInfo('creativeSetCount') > 0) {
      const creativeSets = this.signalListFactory.create(this.creativeSetApi, {
        listId: 'fullCreativeSetsForAdvertiser', baseTable: 'creativeSet', syncPageToUrl: false,
        additionalParams: {advertiser: this.model.id}
      }, this.destroyRef);
      creativeSets.readOnly.set(!!this.readOnly);
      this.creativeSets.set(creativeSets);
      creativeSets.load();
    }

    const creatives = this.signalListFactory.create(this.creativeApi, {
      listId: 'fullCreativesForAdvertiser', baseTable: 'creative', syncPageToUrl: false,
      additionalParams: {advertiser: this.model.id},
      transformResults: (items) => {
        items.forEach((creative) => {
          creative.previewUrl = this.$state.href('nude.preview', {
            creativeId: creative.id,
            networkId: creative.network?.id,
            creativeWidth: creative.width,
            creativeHeight: creative.height,
            lineItemId: creative.lineItemId,
            lineItemCode: creative.lineItemCode
          }, {absolute: true});
        });
        return items;
      }
    }, this.destroyRef);
    creatives.readOnly.set(!!this.readOnly);
    this.creatives.set(creatives);
    creatives.load();
  }

  private scheduleListInit(): void {
    if (this.listInitTimer) {
      clearTimeout(this.listInitTimer);
    }
    // Keep list population out of the input check that creates the tab badges.
    this.listInitTimer = setTimeout(() => {
      if (!this.destroyed) {
        this.initLists();
      }
    }, 0);
  }

  private bufferListCount(state: BufferedSignalValue<number>, list: {totalCount(): number} | null): number {
    return bufferTemplateSignalValue(state, list?.totalCount() || 0, () => !this.destroyed);
  }

  private bufferList<T>(state: BufferedSignalValue<T | null>, list: T | null): T | null {
    return bufferTemplateSignalValue(state, list, () => !this.destroyed);
  }

}
